import { Component, Input, OnDestroy, OnInit, inject } from "@angular/core";
import { Firestore, doc, getDoc } from "@angular/fire/firestore";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subject, firstValueFrom } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import {
  ADD_HOME_SCREEN_DOC_ID,
  A2HS_DISMISSED_SESSION_KEY,
  isA2hsValidRoute,
  upsertDismissedDevice,
} from "./add-home-screen.helpers";
import { SafariAddHomeScreenSheetComponent } from "./safari-add-home-screen-sheet.component";

@Component({
  selector: "app-add-home-screen-banner",
  templateUrl: "./add-home-screen-banner.component.html",
  styleUrls: ["./add-home-screen-banner.component.scss"],
  standalone: true,
  imports: [TranslatePipe],
})
export class AddHomeScreenBannerComponent implements OnInit, OnDestroy {
  private readonly firestore = inject(Firestore);
  private unsubscribe$: Subject<void> = new Subject<void>();
  private dismissed = false;

  public get isDismissed(): boolean {
    const sessionId = sessionStorage.getItem("SessionId");
    return (
      this.dismissed ||
      (!!sessionId && sessionStorage.getItem(A2HS_DISMISSED_SESSION_KEY) === sessionId)
    );
  }

  @Input() notificationData!: Data;

  constructor(
    private route: Router,
    public translate: TranslateService,
    private authService: AuthService,
    private bottomSheet: MatBottomSheet
  ) {
    this.authService?.selectedlanguage$?.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (language: string) => {
        if (language) {
          translate.use(language || "en");
        }
      },
      error: (error) => error,
    });
  }

  async ngOnInit() {
    try {
      await this.authService.getUid("AddHomeScreenBannerComponent");
    } catch {
      // tracking still runs below; uid is only needed for dismiss writes
    }
    this.trackNotification(false);
  }

  trackNotification(isClicked: boolean, isClosed: boolean = false): void {
    this.authService?.trackInlineNotifications(
      CommonVariable.trackAddHomeScreenData,
      isClicked,
      isClosed
    );
  }

  public openSafariSteps(): void {
    this.trackNotification(true, false);
    this.bottomSheet.open(SafariAddHomeScreenSheetComponent, {
      panelClass: "add-home-popup-bottomSheet",
    });
  }

  public dismissNotification(): Promise<void> {
    this.trackNotification(true, true);
    this.dismissed = true;
    const sessionId = sessionStorage.getItem("SessionId");
    if (sessionId) {
      sessionStorage.setItem(A2HS_DISMISSED_SESSION_KEY, sessionId);
    }
    return this.updateDismissedDevices();
  }

  private async updateDismissedDevices(): Promise<void> {
    try {
      const sessionId = sessionStorage.getItem("SessionId");
      if (!sessionId || !this.getValidRoute()) {
        return;
      }

      const userId = await this.authService.getUid("AddHomeScreenBannerComponent.dismiss");
      if (!userId || userId === "null") {
        return;
      }

      // Prefer a merged client array when Firestore is readable; otherwise send the
      // session delta and let updatefirestoredoc merge transactionally (AC3/AC8).
      let dismissed_devices = [{ session_id: sessionId, dismissed: true }];
      try {
        const addHomeScreenRef = doc(
          this.firestore,
          `users/${userId}/inline_notification/${ADD_HOME_SCREEN_DOC_ID}`
        );
        const addHomeScreenSnap = await getDoc(addHomeScreenRef);
        const rawDismissed = addHomeScreenSnap.exists()
          ? addHomeScreenSnap.data()?.dismissed_devices
          : [];
        if (Array.isArray(rawDismissed)) {
          dismissed_devices = upsertDismissedDevice(rawDismissed, sessionId);
        }
      } catch {
        // Local harness / offline path — CF still receives the session dismiss.
      }

      await firstValueFrom(
        this.authService
          .getV2CloudFunction("updatefirestoredoc", {
            path_id: "AhScRn9k2mPqWx7vB4eT",
            expected_user_id: userId,
            doc_data: { dismissed_devices },
            method: "update",
          })
          .pipe(take(1))
      );
    } catch (error) {
      console.error("Caught error dismissing add home screen inline:", error);
    }
  }

  getValidRoute(): string {
    const route = this.route.url?.split("/")?.pop()?.split("?")?.[0];
    if (!isA2hsValidRoute(route)) {
      return "";
    }
    return route;
  }

  getIconUrl(iconUrl: string | undefined): string {
    return this.authService.getIconUrl(iconUrl);
  }

  ngOnDestroy() {
    this.unsubscribe$?.next();
    this.unsubscribe$?.complete();
  }
}
